import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationsService } from '@mt-ng2/notifications-module';
import { IDelivery } from '../../model/interfaces/delivery';
import { DeliveryService } from '../services/delivery.service';
import { BasePaymentComponent } from '../../common/components/base-payment.component';
import { SquarePaymentService } from '../../common/services/square-payment-service';
import { DeviceDetectionService } from '../../common/services/device-detection.service';

@Component({
    selector: 'app-delivery-pre-payment',
    templateUrl: './delivery-pre-payment.component.html',
    styleUrls: ['./delivery-pre-payment.component.css'],
    standalone: false
})
export class DeliveryPrePaymentComponent extends BasePaymentComponent implements OnInit {
    delivery: IDelivery;
    deliveryId: number;
    twicFeeLabel = 'TWIC Fee';
    protected squareLocationId: string;
    protected remainingBalance: number;

    constructor(
        private deliveryService: DeliveryService,
        private route: ActivatedRoute,
        protected override squarePaymentService: SquarePaymentService,
        protected override notificationsService: NotificationsService,
        protected override deviceDetectionService: DeviceDetectionService,
        private router: Router,
    ) {
        super(squarePaymentService, notificationsService, deviceDetectionService);
    }

    ngOnInit(): void {
        this.deliveryId = +this.route.snapshot.paramMap.get('deliveryId');
        this.deliveryService.getById(this.deliveryId).subscribe({
            next: (delivery) => {
                if (!delivery) {
                    this.redirectInProcess = true;
                    void this.router.navigate(['/deliveries']);
                    return;
                }

                this.delivery = delivery;
                this.remainingBalance = this.deliveryService.totalRemainingBalance(this.delivery);
                this.squareLocationId = this.delivery.Warehouse?.Office?.SquareLocationId;
                if (!this.squareLocationId) {
                    this.redirectInProcess = true;
                    this.notificationsService.error('The selected delivery does not have a office with a Square Location ID. Please contact support.');
                    void this.router.navigate(['/deliveries']);
                    return;
                }

                this.deliveryService.isOutsidePrePaymentTimeframe(this.deliveryId).subscribe({
                    next: (isOutsidePrePaymentTimeframe) => {
                        if (isOutsidePrePaymentTimeframe) {
                            this.redirectInProcess = true;
                            this.notificationsService.error('The ability to pre-pay for this delivery is outside the pre-payment time frame.');
                            void this.router.navigate(['/deliveries']);
                            return;
                        }
                    }
                });
            }
        });
    }

    protected handleSuccessfulPayment(token: string): void {
        // Check if carrier has credit available and apply it first
        if (this.delivery.CarrierId && this.delivery.CarrierId > 0) {
            this.deliveryService.checkCarrierCreditEligibility(this.deliveryId).subscribe({
                next: (creditEligibility) => {
                    if (creditEligibility.canUseCredit && creditEligibility.availableCredit >= this.remainingBalance) {
                        // Carrier has sufficient credit, apply it instead of charging card
                        this.applyCarrierCredit();
                    } else {
                        // Not enough credit or no credit available, process with card payment
                        this.processCardPayment(token);
                    }
                },
                error: () => {
                    // Error checking credit, proceed with card payment
                    this.processCardPayment(token);
                }
            });
        } else {
            // No carrier assigned or invalid carrier ID, process with card payment
            this.processCardPayment(token);
        }
    }

    private applyCarrierCredit(): void {
        this.deliveryService.processDeliveryWithCredit(this.deliveryId).subscribe({
            next: (result) => {
                if (result.Success) {
                    this.notificationsService.success(`Your payment of $${this.remainingBalance.toFixed(2)} for delivery ${this.delivery.DeliveryNumber} has been applied to carrier credit.`);
                    this.orderPaid();
                } else {
                    this.notificationsService.error('Failed to apply carrier credit. Please try another payment method.');
                    this.loading = false;
                }
            },
            error: (error) => {
                this.notificationsService.error('Error applying carrier credit. Please try another payment method.');
                  
                console.error('Error processing delivery credit:', error);
                this.loading = false;
            }
        });
    }

    private processCardPayment(token: string): void {
        this.deliveryService.captureSquareOrder(this.delivery.Id, token).subscribe({
            next: () => this.orderPaid(),
            error: () => {
                this.notificationsService.error('There was an error capturing the payment.');
                this.loading = false;
            }
        });
    }

    orderPaid(): void {
        this.notificationsService.success(`Your payment has been successfully processed in the amount of $${this.remainingBalance.toFixed(2)} for delivery ${this.delivery.DeliveryNumber}.`);
        void this.router.navigate(['/deliveries']);            
    }
}
